import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { ShoppingCart, Trash2, Plus, Minus, AlertCircle, Package } from 'lucide-react';
import { Alert, AlertDescription } from './ui/alert';
import { toast } from 'sonner';

const CartModal = ({ isOpen, onClose, cart, onUpdateQuantity, onRemoveItem, onCheckout, user }) => {
  const navigate = useNavigate();
  const [notes, setNotes] = useState('');
  const [isCheckingOut, setIsCheckingOut] = useState(false);

  const subtotal = cart.items?.reduce((sum, item) => sum + item.total_price, 0) || 0;
  const tax = subtotal * 0.05; // 5% GST
  const total = subtotal + tax;

  const creditRemaining = cart.periodSummary?.creditRemaining || 2500;
  const creditUsed = cart.periodSummary?.totalPeriodAmount || 0;
  const creditLimit = cart.periodSummary?.creditLimit || 2500;
  const creditPercentage = (creditUsed / creditLimit) * 100;

  const handleCheckout = async () => {
    if (!user) {
      toast.error('Please log in to place an order');
      navigate('/login');
      return;
    }

    if (cart.items.length === 0) {
      toast.error('Your cart is empty');
      return;
    }

    // Check credit limit
    const newTotal = creditUsed + total;
    if (newTotal > creditLimit) {
      toast.error(`Order exceeds your credit limit. You have $${creditRemaining.toFixed(2)} remaining.`);
      return;
    }

    setIsCheckingOut(true);

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      const token = localStorage.getItem('auth_token');

      const response = await fetch(`${backendUrl}/api/orders/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          items: cart.items.map(item => ({
            product_id: item.product_id,
            product_name: item.product_name,
            quantity: item.quantity,
            unit_price: item.unit_price
          })),
          notes: notes
        })
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(`Order placed successfully! Order #${result.order_id.slice(-8).toUpperCase()}`);
        setNotes('');
        onCheckout(); // This will reload cart and clear it
        onClose();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to place order');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(error.message || 'Failed to place order. Please try again.');
    } finally {
      setIsCheckingOut(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center text-2xl">
            <ShoppingCart className="w-6 h-6 mr-2 text-purple-600" />
            Your Cart
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Credit Limit Status */}
          <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium text-slate-700">Credit Limit</span>
              <span className="text-sm font-semibold">${creditUsed.toFixed(2)} / ${creditLimit.toFixed(2)}</span>
            </div>
            <div className="w-full bg-slate-200 rounded-full h-3">
              <div 
                className={`h-3 rounded-full transition-all ${
                  creditPercentage > 90 ? 'bg-red-500' :
                  creditPercentage > 70 ? 'bg-orange-500' :
                  'bg-green-500'
                }`}
                style={{ width: `${Math.min(creditPercentage, 100)}%` }}
              />
            </div>
            <p className="text-xs text-slate-600 mt-1">
              ${creditRemaining.toFixed(2)} remaining credit
            </p>
          </div>

          {/* Cart Items */}
          {cart.items && cart.items.length > 0 ? (
            <div className="space-y-3">
              {cart.items.map((item) => (
                <div key={item.id} className="flex items-center gap-4 p-4 bg-white border border-slate-200 rounded-lg">
                  <div className="flex-1">
                    <h4 className="font-semibold text-slate-900">{item.product_name}</h4>
                    <p className="text-sm text-slate-600">
                      ${item.unit_price.toFixed(2)} per case
                    </p>
                  </div>

                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 w-8 p-0"
                      onClick={() => onUpdateQuantity(item.id, item.quantity - 1)}
                      disabled={item.quantity <= 1}
                    >
                      <Minus className="w-4 h-4" />
                    </Button>
                    <span className="w-12 text-center font-semibold">{item.quantity}</span>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 w-8 p-0"
                      onClick={() => onUpdateQuantity(item.id, item.quantity + 1)}
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>

                  <div className="text-right min-w-[80px]">
                    <p className="font-bold text-green-600">${item.total_price.toFixed(2)}</p>
                  </div>

                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    onClick={() => onRemoveItem(item.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Package className="w-16 h-16 mx-auto mb-4 text-slate-300" />
              <p className="text-slate-500 text-lg">Your cart is empty</p>
              <Button 
                variant="outline" 
                className="mt-4"
                onClick={onClose}
              >
                Continue Shopping
              </Button>
            </div>
          )}

          {/* Order Notes */}
          {cart.items && cart.items.length > 0 && (
            <>
              <div className="space-y-2">
                <Label htmlFor="order-notes" className="text-slate-700">Order Notes (Optional)</Label>
                <Textarea
                  id="order-notes"
                  placeholder="Special delivery instructions, preferred delivery date, etc..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                />
              </div>

              {/* Order Summary */}
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Subtotal:</span>
                  <span className="font-medium">${subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">GST (5%):</span>
                  <span className="font-medium">${tax.toFixed(2)}</span>
                </div>
                <div className="border-t border-slate-300 pt-2 mt-2">
                  <div className="flex justify-between">
                    <span className="font-semibold text-slate-900">Total:</span>
                    <span className="font-bold text-xl text-green-600">${total.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {/* Credit Warning */}
              {creditRemaining - total < 500 && (
                <Alert className="bg-orange-50 border-orange-200">
                  <AlertCircle className="h-4 w-4 text-orange-600" />
                  <AlertDescription className="text-orange-800">
                    After this order, you'll have ${(creditRemaining - total).toFixed(2)} remaining credit.
                  </AlertDescription>
                </Alert>
              )}

              {/* Checkout Button */}
              <Button
                className="w-full py-6 text-lg font-semibold"
                style={{ background: 'linear-gradient(135deg, #7C3AED, #F97316)' }}
                onClick={handleCheckout}
                disabled={isCheckingOut || !user}
              >
                {isCheckingOut ? 'Placing Order...' : 'Place Order'}
              </Button>

              <p className="text-xs text-center text-slate-500">
                This order will be added to your current billing cycle. Invoice and PAD withdrawal on next billing date (1st or 15th).
              </p>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CartModal;
